/**
 * Intl-based date/duration formatters (P5-1 / MR-45) — a byte-identical
 * replacement for the four `moment` call sites so the MR-46 swap is
 * behaviour-preserving:
 *   - v4 Player `displayDate`          moment(ms).format('MMM DD')       -> formatMonthDay
 *   - v3 PlayerInfo `unixToDateFormat` moment.unix(s).format('MM/DD/YYYY')-> formatMonthDayYear
 *   - v2 LiveAudioPlayer time/dur   moment.utc(ms).format('HH:mm:ss') -> formatDurationClock
 *
 * Proven live against moment@2.30 in test/unit/dateFormat.spec.js (that parity
 * block is removed when moment is dropped at MR-47).
 *
 * Timezone semantics mirror the calls they replace:
 *   - formatMonthDay / formatMonthDayYear read the runtime LOCAL zone (like
 *     `moment(ms)` / `moment.unix(s)` with no `.utc()`); `Intl.DateTimeFormat`
 *     with no `timeZone` option resolves that same local zone.
 *   - formatDurationClock is a UTC elapsed clock (like `moment.utc(ms)`): it reads
 *     the UTC time-of-day via `Date.prototype.getUTC*` — zone-independent, wraps at
 *     24h exactly as moment does (25h -> 01:00:00), and byte-identical for negative
 *     inputs too. `getUTCHours` is always 0-23, so it dodges the `Intl` `24:mm:ss`
 *     day-boundary bug without hand-rolled modulo (which mishandles negatives).
 *
 * Every function returns moment's `'Invalid date'` sentinel for a non-finite /
 * out-of-range epoch (matching `moment(NaN)`/`moment.utc(Infinity)` etc.), so an
 * unguarded call site — `Player.vue`'s `moment(p.timestampCreated * 1000)` and
 * `LiveAudioPlayer.vue`'s `moment.utc(this.duration * 1000)` (duration is
 * `NaN` pre-metadata / `Infinity` for live HLS) — keeps producing a harmless
 * string instead of throwing after the MR-46 swap.
 *
 * ES2015 only (no optional-chaining / nullish / object-spread): the shared `src/`
 * bundle is pinned pre-ES2018 (VUE3_MIGRATION_PLAN Phase 5, Locked #3).
 */

// Constructed once. `Intl.DateTimeFormat` with no `timeZone` resolves the runtime
// local zone, matching moment's local formatting; `en-US` `month:'short'` yields
// the same English 3-letter abbrevs moment's default `en` locale uses.
//
// Caveat (review-fold, LOW / theoretical): the abbreviations come from the
// runtime's ICU/CLDR data, not from a table we control. CLDR 42 (ICU 72, ~Chrome
// 106-111, Oct-2022→Mar-2023) briefly rendered abbreviated September as `Sept`;
// CLDR 43 reverted it to `Sep` (matching moment's `en`). So on a browser pinned to
// that narrow, now-superseded ICU window `formatMonthDay(<Sep date>)` would yield
// `Sept DD` where moment gave `Sep DD`. No current runtime reproduces it and it
// self-heals as ICU updates; the dateFormat.spec `MS_TABLE` includes a September
// date with a hardcoded `Sep` oracle, so CI on a CLDR-42 runtime would catch it.
const monthDayFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: '2-digit',
});

const monthDayYearFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

// Pull a part value by type from `formatToParts` output (avoids relying on the
// locale's part order / separators — we assemble the shape ourselves).
const partValue = (parts, type) => {
  for (let i = 0; i < parts.length; i += 1) {
    if (parts[i].type === type) return parts[i].value;
  }
  return '';
};

const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);

// moment's sentinel for any non-finite / out-of-range epoch. `new Date(x)` is
// Invalid (getTime() === NaN) for NaN, ±Infinity, and values beyond the ±8.64e15
// Date range — exactly the set moment renders as 'Invalid date'.
const INVALID_DATE = 'Invalid date';
const isInvalidDate = (date) => Number.isNaN(date.getTime());

// moment `YYYY` zero-pads years to at least 4 digits (year 500 -> '0500'); Intl
// `year:'numeric'` does not. Left-pad to 4, leave 5+-digit years untouched.
const pad4Year = (year) => (year.length < 4 ? `0000${year}`.slice(-4) : year);

/**
 * `moment(epochMs).format('MMM DD')` — English abbreviated month + zero-padded
 * day-of-month, in the LOCAL zone. e.g. `Jul 20`.
 * @param {number} epochMs epoch milliseconds
 * @returns {string}
 */
export const formatMonthDay = (epochMs) => {
  const date = new Date(epochMs);
  if (isInvalidDate(date)) return INVALID_DATE;
  const parts = monthDayFormatter.formatToParts(date);
  return `${partValue(parts, 'month')} ${partValue(parts, 'day')}`;
};

/**
 * `moment.unix(unixSeconds).format('MM/DD/YYYY')` — zero-padded MM/DD + 4-digit
 * year, in the LOCAL zone. e.g. `07/20/2026`.
 * @param {number} unixSeconds unix epoch seconds
 * @returns {string}
 */
export const formatMonthDayYear = (unixSeconds) => {
  const date = new Date(unixSeconds * 1000);
  if (isInvalidDate(date)) return INVALID_DATE;
  const parts = monthDayYearFormatter.formatToParts(date);
  return `${partValue(parts, 'month')}/${partValue(parts, 'day')}/${pad4Year(partValue(parts, 'year'))}`;
};

/**
 * `moment.utc(epochMs).format('HH:mm:ss')` — an elapsed 24h clock read from the
 * UTC time-of-day. Wraps at 24h and is byte-identical to moment for negatives.
 * @param {number} epochMs elapsed milliseconds
 * @returns {string}
 */
export const formatDurationClock = (epochMs) => {
  const date = new Date(epochMs);
  if (isInvalidDate(date)) return INVALID_DATE;
  return `${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}:${pad2(date.getUTCSeconds())}`;
};
